import React, { useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

// Mentor-triggered "switch track" action for the common case of a student
// deciding to change between Scientific Method and Engineering Design after
// Step 3 (background research) is already done. Calls switch-project-track,
// which archives (never deletes) Step 2's track-specific fields and any
// Step 4/5 work, keeps Step 3 exactly as written, and re-opens Step 2 for
// re-approval under the new track. See TrackSwitchArchivePanel for viewing
// what got archived.

type Track = 'scientific_method' | 'engineering_design'

const TRACK_LABEL: Record<Track, string> = {
  scientific_method:  'Scientific Method',
  engineering_design: 'Engineering Design',
}

export default function TrackSwitchPanel({ projectId, currentTrack, studentName, onSwitched }: {
  projectId:    string
  currentTrack: Track
  studentName?: string
  onSwitched:   () => void
}) {
  const otherTrack: Track = currentTrack === 'scientific_method' ? 'engineering_design' : 'scientific_method'
  const [open,  setOpen]  = useState(false)
  const [busy,  setBusy]  = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirmSwitch() {
    setBusy(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('switch-project-track', {
        body: { project_id: projectId, new_track: otherTrack },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      setOpen(false)
      onSwitched()
    } catch (e: any) {
      setError(e.message ?? 'Could not switch the track — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{
      mb: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
    }}>
      <SyncAltIcon fontSize="small" color="action" />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
        Track:{' '}
        <Chip size="small" label={TRACK_LABEL[currentTrack]} sx={{ fontWeight: 700, ml: 0.5 }} />
      </Typography>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        Switch to {TRACK_LABEL[otherTrack]}
      </Button>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Switch {studentName || 'this student'} to {TRACK_LABEL[otherTrack]}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>This will:</Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 1.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">
              Re-open <strong>Step 2</strong> for the {TRACK_LABEL[otherTrack]} track — the student redoes it and resubmits for your approval.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Keep Step 3's background research exactly as written.</strong> It resets to draft so the student can quickly resubmit it once Step 2 is approved, but no content is deleted or rewritten.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Archive — never delete — any Step 4/5 work</strong> already done under the old track. It's saved and viewable below the project header; the student starts those steps fresh under the new track.
            </Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmSwitch} disabled={busy}>
            {busy ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Switching…</> : `Switch to ${TRACK_LABEL[otherTrack]}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
